import { useEffect, useRef } from "react";

// Refresh-recovery window: a mirror older than this is treated as stale and dropped rather than
// restored, so this behaves like reload/crash recovery — not a durable "resume days later" draft.
const TTL_MS = 30 * 60 * 1000;

const keyFor = (orgId, recruiterId, scopeId) =>
    `sourcing_seq_builder_draft_${orgId || "anon"}_${recruiterId || "anon"}_${scopeId || "new"}`;

/**
 * Mirrors the sequence builder's { form, currentStep } to localStorage while the wizard is dirty,
 * restores it once on mount if it's fresh (surviving a page reload), and flushes it synchronously
 * on unload so the last edits aren't lost. Intentional exits (Cancel / back) are handled by the
 * caller's discard modal, which calls clear(). Returns { clear } to wipe the mirror on
 * create / discard.
 *
 * Params: orgId, recruiterId, scopeId (scope the key — the sequence id when editing, "new" when
 * creating, so a create-draft and an edit-draft never collide) — dirty (bool), snapshot
 * ({ form, currentStep }), onRestore(saved) — called at most once with the restored snapshot.
 */
export function useSequenceDraft({ orgId, recruiterId, scopeId, dirty, snapshot, onRestore }) {
    const key = keyFor(orgId, recruiterId, scopeId);

    // Latest values for the unload handler, which is registered once and must not close over stale state.
    const snapshotRef = useRef(snapshot);
    const dirtyRef = useRef(dirty);
    const keyRef = useRef(key);
    snapshotRef.current = snapshot;
    dirtyRef.current = dirty;
    keyRef.current = key;

    // Restore exactly once, as soon as the user id (hence the real key) is known.
    const restoredRef = useRef(false);
    useEffect(() => {
        if (restoredRef.current || !orgId) return;
        restoredRef.current = true;
        const raw = localStorage.getItem(key);
        if (!raw) return;
        try {
            const saved = JSON.parse(raw);
            if (saved?.form && Date.now() - (saved.savedAt || 0) < TTL_MS) onRestore(saved);
            else localStorage.removeItem(key);
        } catch {
            localStorage.removeItem(key);
        }
    }, [key, orgId, onRestore]);

    // Debounced mirror while dirty.
    useEffect(() => {
        if (!dirty || !orgId) return undefined;
        const t = setTimeout(() => {
            localStorage.setItem(key, JSON.stringify({ ...snapshot, savedAt: Date.now() }));
        }, 500);
        return () => clearTimeout(t);
    }, [snapshot, dirty, orgId, key]);

    // Flush synchronously and warn the browser on reload / close while dirty.
    useEffect(() => {
        const handler = (e) => {
            if (!dirtyRef.current) return;
            localStorage.setItem(
                keyRef.current,
                JSON.stringify({ ...snapshotRef.current, savedAt: Date.now() })
            );
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, []);

    const clear = () => localStorage.removeItem(keyRef.current);
    return { clear };
}
